/**
 * Public Authentication Service
 * Handles authentication operations for public users (customers)
 * 
 * Features:
 * - Registration and login
 * - Token management
 * - Profile management
 * - Secure API requests
 * 
 * @author Zubduq 
 * @version 1.0.0
 */

class PublicAuthService {
  constructor() {
    this.apiBase = '/api/public';
    this.storageKeys = {
      token: 'publicToken',
      refreshToken: 'publicRefreshToken',
      user: 'publicUser',
      userType: 'userType'
    };
  }

  /**
   * Register a new public user
   */
  async register(name, email, mobile) {
    try {
      // Get CSRF token
      const csrfResponse = await fetch('/api/csrf-token');
      if (!csrfResponse.ok) {
        throw new Error('Security validation failed');
      }
      const { csrfToken } = await csrfResponse.json();

      // Attempt registration
      const response = await fetch(`${this.apiBase}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ name, email, mobile })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Store authentication data
      this.storeAuthData(data);

      return data;
    } catch (error) {
      console.error('Public registration error:', error);
      throw error;
    }
  }

  /**
   * Login public user
   */
  async login(email, mobile) {
    try {
      // Get CSRF token
      const csrfResponse = await fetch('/api/csrf-token');
      if (!csrfResponse.ok) {
        throw new Error('Security validation failed');
      }
      const { csrfToken } = await csrfResponse.json();

      // Attempt login
      const response = await fetch(`${this.apiBase}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ email, mobile })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store authentication data
      this.storeAuthData(data);

      return data;
    } catch (error) {
      console.error('Public login error:', error);
      throw error;
    }
  }

  /**
   * Get user profile
   */
  async getProfile() {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${this.apiBase}/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this.clearAuthData();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(data.error || 'Failed to get profile');
      }

      return data;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(name, email, mobile) {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Get CSRF token
      const csrfResponse = await fetch('/api/csrf-token');
      if (!csrfResponse.ok) {
        throw new Error('Security validation failed');
      }
      const { csrfToken } = await csrfResponse.json();

      const response = await fetch(`${this.apiBase}/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ name, email, mobile })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this.clearAuthData();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(data.error || 'Failed to update profile');
      }

      // Update stored user data
      if (data.user) {
        localStorage.setItem(this.storageKeys.user, JSON.stringify(data.user));
      }

      return data;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  /**
   * Logout user
   */
  async logout() {
    try {
      const token = this.getToken();
      if (token) {
        // Call logout endpoint
        await fetch(`${this.apiBase}/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local data
      this.clearAuthData();
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${this.apiBase}/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Token refresh failed');
      }

      // Store new authentication data
      this.storeAuthData(data);

      return data;
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearAuthData();
      throw error;
    }
  }

  /**
   * Store authentication data
   */
  storeAuthData(data) {
    if (data.token) {
      localStorage.setItem(this.storageKeys.token, data.token);
    }
    if (data.refreshToken) {
      localStorage.setItem(this.storageKeys.refreshToken, data.refreshToken);
    }
    if (data.user) {
      localStorage.setItem(this.storageKeys.user, JSON.stringify(data.user));
    }
    localStorage.setItem(this.storageKeys.userType, 'public');
  }

  /**
   * Clear authentication data
   */
  clearAuthData() {
    Object.values(this.storageKeys).forEach(key => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Get stored authentication token
   */
  getToken() {
    return localStorage.getItem(this.storageKeys.token);
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken() {
    return localStorage.getItem(this.storageKeys.refreshToken);
  }

  /**
   * Get stored user data
   */
  getUser() {
    const userJson = localStorage.getItem(this.storageKeys.user);
    return userJson ? JSON.parse(userJson) : null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * Check if current user is public user
   */
  isPublicUser() {
    return localStorage.getItem(this.storageKeys.userType) === 'public';
  }

  /**
   * Get user type
   */
  getUserType() {
    return localStorage.getItem(this.storageKeys.userType) || null;
  }
}

// Create and export singleton instance
const publicAuthService = new PublicAuthService();

// Make available globally for compatibility
if (typeof window !== 'undefined') {
  window.PublicAuthService = publicAuthService;
}

export default publicAuthService;
